import React from 'react'

interface MysticalPortalProps {
  size?: number
  className?: string
  animated?: boolean
  gender?: 'boy' | 'girl' | 'neutral'
}

const MysticalPortal: React.FC<MysticalPortalProps> = ({ 
  size = 150, 
  className = '', 
  animated = true,
  gender = 'neutral'
}) => {
  const getPortalColor = () => {
    switch (gender) {
      case 'boy': return { primary: '#3b82f6', secondary: '#60a5fa', accent: '#1e40af' }
      case 'girl': return { primary: '#ec4899', secondary: '#f9a8d4', accent: '#be185d' }
      default: return { primary: '#667eea', secondary: '#764ba2', accent: '#4c1d95' }
    }
  }

  const colors = getPortalColor()

  return (
    <div className={className} style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 150 150"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id={`portalGradient-${gender}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
            <stop offset="30%" stopColor={colors.primary} />
            <stop offset="70%" stopColor={colors.secondary} />
            <stop offset="100%" stopColor={colors.accent} />
          </radialGradient>
          
          <radialGradient id={`innerPortal-${gender}`} cx="50%" cy="50%" r="40%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.8)" />
            <stop offset="50%" stopColor={`${colors.primary}80`} />
            <stop offset="100%" stopColor="rgba(0,0,0,0.3)" />
          </radialGradient>

          <filter id="portalGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          <pattern id="energyPattern" patternUnits="userSpaceOnUse" width="20" height="20">
            <circle cx="10" cy="10" r="2" fill={colors.primary} opacity="0.3">
              {animated && (
                <animate attributeName="opacity" values="0.1;0.6;0.1" dur="3s" repeatCount="indefinite" />
              )}
            </circle>
          </pattern>
        </defs>

        {/* Outer Energy Rings */}
        {animated && (
          <g>
            <circle cx="75" cy="75" r="70" fill="none" stroke={colors.primary} strokeWidth="2" opacity="0.3">
              <animate attributeName="r" values="65;75;65" dur="4s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.1;0.4;0.1" dur="4s" repeatCount="indefinite" />
            </circle>
            <circle cx="75" cy="75" r="75" fill="none" stroke={colors.secondary} strokeWidth="1" opacity="0.2">
              <animate attributeName="r" values="70;80;70" dur="5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.05;0.3;0.05" dur="5s" repeatCount="indefinite" />
            </circle>
          </g>
        )}

        {/* Main Portal Circle */}
        <circle 
          cx="75" 
          cy="75" 
          r="60" 
          fill={`url(#portalGradient-${gender})`}
          filter="url(#portalGlow)"
        >
          {animated && (
            <animate attributeName="r" values="58;62;58" dur="6s" repeatCount="indefinite" />
          )}
        </circle>

        {/* Inner Swirling Portal */}
        <circle cx="75" cy="75" r="45" fill={`url(#innerPortal-${gender})`}>
          {animated && (
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 75 75;360 75 75"
              dur="20s"
              repeatCount="indefinite"
            />
          )}
        </circle>

        {/* Energy Pattern Overlay */}
        <circle cx="75" cy="75" r="50" fill="url(#energyPattern)" opacity="0.5" />

        {/* Swirling Energy Streams */}
        {animated && (
          <g>
            <path 
              d="M 35 75 Q 55 55 75 75 Q 95 95 115 75" 
              stroke={colors.primary} 
              strokeWidth="3" 
              fill="none" 
              opacity="0.6"
            >
              <animate 
                attributeName="stroke-dasharray" 
                values="0,100;50,50;100,0" 
                dur="3s" 
                repeatCount="indefinite" 
              />
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0 75 75;360 75 75"
                dur="8s"
                repeatCount="indefinite"
              />
            </path>
            
            <path 
              d="M 75 35 Q 95 55 75 75 Q 55 95 75 115" 
              stroke={colors.secondary} 
              strokeWidth="2" 
              fill="none" 
              opacity="0.5"
            >
              <animate 
                attributeName="stroke-dasharray" 
                values="0,80;40,40;80,0" 
                dur="4s" 
                repeatCount="indefinite" 
              />
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0 75 75;-360 75 75"
                dur="12s"
                repeatCount="indefinite"
              />
            </path>
          </g>
        )}        {/* Central Vortex */}
        <circle cx="75" cy="75" r="15" fill="rgba(255,255,255,0.8)">
          {animated && (
            <>
              <animate attributeName="r" values="12;18;12" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.6;1;0.6" dur="2s" repeatCount="indefinite" />
            </>
          )}
        </circle>

        {/* Mystical Symbols Around Portal */}
        <g className="portal-runes" opacity="0.7">
          <g transform="translate(75,25)">
            <text fontSize="12" textAnchor="middle" fill={colors.accent}>☾</text>
            {animated && (
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0 0 0;360 0 0"
                dur="30s"
                repeatCount="indefinite"
              />
            )}
          </g>
          
          <g transform="translate(125,75)">
            <text fontSize="12" textAnchor="middle" fill={colors.accent}>✦</text>
            {animated && (
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0 0 0;-360 0 0"
                dur="25s"
                repeatCount="indefinite"
              />
            )}
          </g>
          
          <g transform="translate(75,125)">
            <text fontSize="12" textAnchor="middle" fill={colors.accent}>◈</text>
            {animated && (
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0 0 0;360 0 0"
                dur="35s"
                repeatCount="indefinite"
              />
            )}
          </g>
          
          <g transform="translate(25,75)">
            <text fontSize="12" textAnchor="middle" fill={colors.accent}>✧</text>
            {animated && (
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="0 0 0;-360 0 0"
                dur="28s"
                repeatCount="indefinite"
              />
            )}
          </g>
        </g>

        {/* Floating Energy Particles */}
        {animated && (
          <g className="energy-particles">
            <circle cx="45" cy="45" r="1.5" fill={colors.primary}>
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0;30,30;0,0"
                dur="6s"
                repeatCount="indefinite"
              />
              <animate attributeName="opacity" values="0.3;1;0.3" dur="6s" repeatCount="indefinite" />
            </circle>
            
            <circle cx="105" cy="45" r="1" fill={colors.secondary}>
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0;-30,30;0,0"
                dur="8s"
                repeatCount="indefinite"
              />
              <animate attributeName="opacity" values="0.2;0.8;0.2" dur="8s" repeatCount="indefinite" />
            </circle>
            
            <circle cx="105" cy="105" r="1.2" fill={colors.accent}>
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0;-30,-30;0,0"
                dur="7s"
                repeatCount="indefinite"
              />
              <animate attributeName="opacity" values="0.4;0.9;0.4" dur="7s" repeatCount="indefinite" />
            </circle>
            
            <circle cx="45" cy="105" r="1.8" fill={colors.primary}>
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0;30,-30;0,0"
                dur="5s"
                repeatCount="indefinite"
              />
              <animate attributeName="opacity" values="0.1;0.7;0.1" dur="5s" repeatCount="indefinite" />
            </circle>
          </g>
        )}
      </svg>
    </div>
  )
}

export default MysticalPortal
